import { ConsentSource } from '@liveauctioneers/hammer-ui-core/cookieConsent';
import { useEffect, useRef } from 'react';

import { useTrackCookieConsentAnalytics } from '@/utils/amplitude/analytics';

const COOKIEBOT_DIALOG_ID = 'CybotCookiebotDialog';
const COOKIEBOT_DETAILS_BUTTON_ID = 'CybotCookiebotDialogBodyButtonDetails';

/**
 * Attaches analytics tracking to the native Cookiebot dialog events.
 * Only active when the custom CookieConsent UI is NOT shown (i.e. shouldSuppressNativeCookiebotDialog is false).
 */
export const useNativeCookiebotTracking = (enabled: boolean) => {
    const { onBannerViewed, onPreferencesOpened, onPreferencesSaved } = useTrackCookieConsentAnalytics();
    const hasTrackedBannerViewed = useRef(false);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const handleDialogDisplay = () => {
            // Guard against double-firing: CookiebotOnDialogDisplay may fire before React hydrates,
            // so we check the DOM on mount AND listen for the event — only track once.
            if (hasTrackedBannerViewed.current) {
                return;
            }
            hasTrackedBannerViewed.current = true;

            onBannerViewed();

            // Details button is in the DOM once the dialog is shown.
            // { once: true } auto-removes after the first click — dialog shows once per session.
            document
                .getElementById(COOKIEBOT_DETAILS_BUTTON_ID)
                ?.addEventListener('click', () => onPreferencesOpened(ConsentSource.Banner), { once: true });
        };

        const handleAccept = () => {
            const consent = window.Cookiebot?.consent;
            onPreferencesSaved({
                consent: {
                    marketing: consent?.marketing ?? false,
                    necessary: true,
                    preferences: consent?.preferences ?? false,
                    statistics: consent?.statistics ?? false,
                },
                source: ConsentSource.Banner,
            });
        };

        const handleDecline = () => {
            onPreferencesSaved({
                consent: {
                    marketing: false,
                    necessary: true,
                    preferences: false,
                    statistics: false,
                },
                source: ConsentSource.Banner,
            });
        };

        window.addEventListener('CookiebotOnDialogDisplay', handleDialogDisplay);
        window.addEventListener('CookiebotOnAccept', handleAccept);
        window.addEventListener('CookiebotOnDecline', handleDecline);

        // Cookiebot loads in <head> and fires CookiebotOnDialogDisplay before React hydrates.
        // If the dialog is already in the DOM and the user hasn't responded yet, track it now.
        const dialogAlreadyShown = !!document.getElementById(COOKIEBOT_DIALOG_ID) && !window.Cookiebot?.hasResponse;
        if (dialogAlreadyShown) {
            handleDialogDisplay();
        }

        return () => {
            window.removeEventListener('CookiebotOnDialogDisplay', handleDialogDisplay);
            window.removeEventListener('CookiebotOnAccept', handleAccept);
            window.removeEventListener('CookiebotOnDecline', handleDecline);
        };
    }, [enabled, onBannerViewed, onPreferencesOpened, onPreferencesSaved]);
};
